import React, { createContext, useContext, useEffect, useState } from 'react';

interface PrivacyContextType {
  privacyMode: boolean;
  togglePrivacyMode: () => void;
}

const PrivacyContext = createContext<PrivacyContextType | undefined>(undefined);

const STORAGE_KEY = 'calendar-privacy-mode';

export function PrivacyProvider({ children }: { children: React.ReactNode }) {
  const [privacyMode, setPrivacyMode] = useState<boolean>(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved === 'true';
  });

  // Persist to localStorage when changed
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, String(privacyMode));
  }, [privacyMode]);

  // Apply privacy class to document for CSS-based blurring
  useEffect(() => {
    const root = document.documentElement;
    if (privacyMode) {
      root.classList.add('privacy-mode');
    } else {
      root.classList.remove('privacy-mode');
    }
  }, [privacyMode]);

  const togglePrivacyMode = () => {
    setPrivacyMode(prev => !prev);
  };

  return (
    <PrivacyContext.Provider value={{
      privacyMode,
      togglePrivacyMode
    }}>
      {children}
    </PrivacyContext.Provider>
  );
}

export function usePrivacy() {
  const context = useContext(PrivacyContext);
  if (context === undefined) {
    throw new Error('usePrivacy must be used within a PrivacyProvider');
  }
  return context;
}
